import type Stripe from 'stripe'
import type { ArbitraryString } from '../utils/global'

export namespace Billing {
    type G10CurrencyCode =
        | 'AUD' // Australia Australian dollar
        | 'CAD' // Canada Canadian dollar
        | 'EUR' // European Union Euro
        | 'JPY' // Japan Japanese yen
        | 'NZD' // New Zealand New Zealand dollar
        | 'NOK' // Norway Norwegian krone
        | 'GBP' // United Kingdom Pound sterling
        | 'SEK' // Sweden Swedish krona
        | 'CHF' // Switzerland Swiss franc
        | 'USD' // United States United States dollar

    /**
     * G10 Currency Code ('AUD', 'CAD', 'EUR', 'JPY', 'NZD', 'NOK', 'GBP', 'SEK', 'CHF', 'USD') or anything else
     */
    export type CurrencyCode = G10CurrencyCode | ArbitraryString
    export type LowercaseCurrencyCode = Lowercase<G10CurrencyCode> | ArbitraryString

    export interface CurrentPlan {
        id: string
        name: string
        price: number
        currency: LowercaseCurrencyCode
        account_limit: number | null
        spend_limit: number | null
        has_reports_access: boolean
        period_interval: 'month' | 'quarter' | 'year'
        enforced: boolean
    }

    export interface OpteoCustomer extends Stripe.Customer {
        cc_up_front?: boolean
        default_payment_method?: Stripe.PaymentMethod
    }

    export type OpteoSubscriptionStatus =
        | Stripe.Subscription.Status
        | 'canceled_at_period_end'
        | 'inactive'

    export interface SubscriptionPlan {
        itemId: string
        priceId: string
        productId: string | null
        currency: string
        unitAmount: number | null
        interval: 'month' | 'year'
        intervalCount: number
        currentPeriodStart: number // unix seconds
        currentPeriodEnd: number // unix seconds
    }

    export interface SubscriptionOption {
        itemId: string
        priceId: string
        currency: string
        quantity: number
    }

    export interface OpteoSubscription extends Omit<Stripe.Subscription, 'status'> {
        status_real?: 'active'
        status: OpteoSubscriptionStatus
        subscriptionPlan: SubscriptionPlan | null
        subscriptionOptions: SubscriptionOption[]
        latest_invoice_amount_remaining: number | null
    }

    export enum LegacyPlan {
        Basic = 'plan-v4-s',
        Professional = 'plan-v4-m',
        Agency = 'plan-v4-l',
        Enterprise = 'plan-v4-xl',
        YearlyBasic = 'plan-v4-s-yearly',
        YearlyProfessional = 'plan-v4-m-yearly',
        YearlyAgency = 'plan-v4-l-yearly',
        YearlyEnterprise = 'plan-v4-xl-yearly',
    }

    export enum StandardPlan {
        Basic = 'plan-v5-s',
        Professional = 'plan-v5-m',
        Agency = 'plan-v5-l',
        Enterprise = 'plan-v5-xl',
        YearlyBasic = 'plan-v5-s-yearly',
        YearlyProfessional = 'plan-v5-m-yearly',
        YearlyAgency = 'plan-v5-l-yearly',
        YearlyEnterprise = 'plan-v5-xl-yearly',
    }

    export enum SpecialPlan {
        System = 'plan-system',
        AgencyGrowth = 'plan-agency-growth',
        TikTokAdvertiserPartner = 'plan-tiktok-advertiser-partner',
        EnterpriseTrial = 'plan-v5-enterprise-trial',
    }

    export const Coupon = {
        ReportUpgrades20Percent3Months: 'report-upgrades-20x3',
    } as const

    export const FreeTrialLengthInDays = 14

    export enum CouponApplicationError {
        COUPON_NOT_APPLICABLE_TO_PRODUCT = 'COUPON_NOT_APPLICABLE_TO_PRODUCT',
        NO_SUBSCRIPTION_OR_PRODUCT = 'NO_SUBSCRIPTION_OR_PRODUCT',
        INVALID_CODE = 'INVALID_CODE',
        UNKNOWN_ERROR = 'UNKNOWN_ERROR',
    }

    export type ExtendedStripeCoupon = Stripe.Coupon & {
        promotionCode: Stripe.PromotionCode | null
    }
    export interface CouponApplicationResult {
        coupon?: ExtendedStripeCoupon
        error?: string
    }

    export type BoltOnKind = 'credits' | 'chat'
    export type BoltOnPurchaseType = 'one-time' | 'recurring'
    export type BoltOnLength = 'month' | 'quarter' | 'year'

    export interface BoltOn {
        id: number
        group_id: number
        kind: BoltOnKind
        title: string
        value: number
        length: BoltOnLength | null
        purchase_type: BoltOnPurchaseType
        count: number | null
        stripe_subscription_item_id: string | null
        created_at: string
    }

    export type BoltOnInput = Omit<
        BoltOn,
        'id' | 'group_id' | 'stripe_subscription_item_id' | 'created_at'
    >
}
